import { UseQueryResult, useQuery } from '@tanstack/react-query';

import {
  getUnreadNotifications,
  markNotificationsAsRead,
} from '../api/notifications';
import {
  GetNotificationsProps,
  NotificationData,
  NotificationKind,
} from '../api/notifications/types';
import { canSee } from '../helpers/roleAccess';
import { unwrap } from '../lib/envelope';
import { queryClient } from '../lib/queryClient';
import { useCurrentRole } from './useCurrentRole';

const KINDS = [NotificationKind.PREFLIGHT_REPORT_RETURNED];
const PAGE_SIZE = 50;

export const returnedReportsKey = [
  'notifications',
  NotificationKind.PREFLIGHT_REPORT_RETURNED,
] as const;

const fetchReturnedReports = ({
  signal,
}: {
  signal?: AbortSignal;
}): Promise<GetNotificationsProps> =>
  getUnreadNotifications(KINDS, 1, PAGE_SIZE, signal).then(unwrap);

/**
 * Unread "laudo devolvido" notifications (the source sent a report version
 * back), for the bell and the notifications page.
 *
 * The back-end does not push these over the websocket, so the query polls:
 * every minute and whenever the window regains focus. Only for roles that can
 * open the pre-flight — for anyone else the link would lead nowhere.
 */
export function useReturnedReports(): UseQueryResult<GetNotificationsProps> & {
  notifications: NotificationData[];
  unread: number;
} {
  const role = useCurrentRole();
  const query = useQuery({
    queryKey: returnedReportsKey,
    queryFn: fetchReturnedReports,
    enabled: canSee('preflight', role),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const notifications = query.data?.data ?? [];
  return {
    ...query,
    notifications,
    unread: query.data?.detail.total_records ?? notifications.length,
  };
}

/**
 * Marks as read every unread "laudo devolvido" of one pre-flight request.
 * Called when its detail is opened: once the reviewer has seen the banner the
 * bell must not keep pointing at it. Silent: a failure only leaves the alert.
 */
export async function markReturnedReportsSeen(
  preflightId: number
): Promise<void> {
  // Fresh read, not the cache: opened from a pasted link or a reload the
  // detail can arrive before the bell's first fetch, and a cached page can
  // miss a return that came in since the last poll. Shares an in-flight
  // request with the bell instead of firing a second one.
  let current: GetNotificationsProps;
  try {
    current = await queryClient.fetchQuery({
      queryKey: returnedReportsKey,
      queryFn: fetchReturnedReports,
      staleTime: 0,
    });
  } catch {
    return;
  }
  const ids = (current.data ?? [])
    .filter((item) => item.resource_id === preflightId)
    .map((item) => item.id);
  if (ids.length === 0) return;
  try {
    await markNotificationsAsRead(ids);
  } catch {
    return;
  }
  await queryClient.invalidateQueries({ queryKey: returnedReportsKey });
}
